import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, ChevronUp, ChevronDown, RotateCcw, Save, Sprout, Beaker, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const emptyPhase = () => ({ name: '', duration_days: 1, action_required: false, harvest: false, description: '' });

const CycleConfig = () => {
  const [activeTab, setActiveTab] = useState('growing'); // 'growing' or 'composting'
  const [growing, setGrowing] = useState([]);
  const [composting, setComposting] = useState([]);
  const [meta, setMeta] = useState({ growing: {}, composting: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState({ growing: false, composting: false });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [g, c] = await Promise.all([
        axios.get(`${API}/cycle-templates/growing`, { headers: auth() }),
        axios.get(`${API}/cycle-templates/composting`, { headers: auth() }),
      ]);
      setGrowing(g.data.phases || []);
      setComposting(c.data.phases || []);
      setMeta({
        growing: { total_days: g.data.total_days, is_default: g.data.is_default, updated_at: g.data.updated_at },
        composting: { total_days: c.data.total_days, is_default: c.data.is_default, updated_at: c.data.updated_at },
      });
      setDirty({ growing: false, composting: false });
    } catch (e) {
      toast.error('Failed to load cycle templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const phases = activeTab === 'growing' ? growing : composting;
  const setPhases = activeTab === 'growing' ? setGrowing : setComposting;
  const markDirty = () => setDirty((d) => ({ ...d, [activeTab]: true }));

  const updatePhase = (idx, key, value) => {
    const next = [...phases];
    next[idx] = { ...next[idx], [key]: value };
    setPhases(next);
    markDirty();
  };

  const addPhase = (after = -1) => {
    const next = [...phases];
    next.splice(after + 1, 0, emptyPhase());
    setPhases(next);
    markDirty();
  };

  const removePhase = (idx) => {
    if (phases.length <= 1) return toast.error('At least one phase required');
    if (!window.confirm(`Remove phase "${phases[idx].name || 'unnamed'}"?`)) return;
    const next = phases.filter((_, i) => i !== idx);
    setPhases(next);
    markDirty();
  };

  const movePhase = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= phases.length) return;
    const next = [...phases];
    [next[idx], next[target]] = [next[target], next[idx]];
    setPhases(next);
    markDirty();
  };

  const totalDays = phases.reduce((s, p) => s + (parseInt(p.duration_days, 10) || 0), 0);

  const save = async () => {
    if (phases.some((p) => !p.name.trim())) return toast.error('Every phase must have a name');
    if (phases.some((p) => !p.duration_days || p.duration_days < 1)) return toast.error('Every phase must have duration ≥ 1 day');
    try {
      setSaving(true);
      const res = await axios.put(`${API}/cycle-templates/${activeTab}`, { phases }, { headers: auth() });
      toast.success(res.data?.message || 'Saved');
      setMeta((m) => ({ ...m, [activeTab]: { ...m[activeTab], total_days: res.data.total_days, is_default: false } }));
      setDirty((d) => ({ ...d, [activeTab]: false }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const resetDefault = async () => {
    if (!window.confirm(`Reset ${activeTab} template to default? Your customisations will be lost.`)) return;
    try {
      const res = await axios.post(`${API}/cycle-templates/${activeTab}/reset`, {}, { headers: auth() });
      if (activeTab === 'growing') setGrowing(res.data.phases);
      else setComposting(res.data.phases);
      setDirty((d) => ({ ...d, [activeTab]: false }));
      toast.success('Reset to default');
      load();
    } catch (e) {
      toast.error('Reset failed');
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading templates...</div>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Cycle Configuration</span>
            <Badge variant="outline" className="text-xs">
              Changes apply to NEW batches/cycles only — running cycles are unaffected
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 border-b border-gray-200 mb-4">
            <button
              data-testid="tab-growing"
              onClick={() => setActiveTab('growing')}
              className={`px-4 py-2 -mb-px font-medium border-b-2 ${activeTab === 'growing' ? 'text-[#1A4D2E] border-[#1A4D2E]' : 'text-gray-500 border-transparent'}`}
            >
              <Sprout className="w-4 h-4 inline mr-2" />Growing ({meta.growing.total_days || 0}d)
            </button>
            <button
              data-testid="tab-composting"
              onClick={() => setActiveTab('composting')}
              className={`px-4 py-2 -mb-px font-medium border-b-2 ${activeTab === 'composting' ? 'text-[#1A4D2E] border-[#1A4D2E]' : 'text-gray-500 border-transparent'}`}
            >
              <Beaker className="w-4 h-4 inline mr-2" />Composting ({meta.composting.total_days || 0}d)
            </button>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={resetDefault}><RotateCcw className="w-3.5 h-3.5 mr-1" />Reset default</Button>
              <Button size="sm" className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" onClick={save} disabled={saving || !dirty[activeTab]} data-testid="save-template">
                <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Saving...' : (dirty[activeTab] ? 'Save' : 'Saved')}
              </Button>
            </div>
          </div>

          {dirty[activeTab] && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md p-2 mb-3 text-sm text-amber-800">
              <AlertCircle className="w-4 h-4" />
              You have unsaved changes. Total cycle length: <strong>{totalDays} days</strong>
            </div>
          )}

          <div className="space-y-2">
            {phases.map((p, idx) => (
              <div key={idx} className="flex items-start gap-2 p-3 border border-gray-200 rounded-lg bg-white">
                <div className="flex flex-col gap-1 pt-7">
                  <button onClick={() => movePhase(idx, -1)} disabled={idx === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => movePhase(idx, 1)} disabled={idx === phases.length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                </div>
                <div className="w-7 text-center pt-7 text-gray-500 font-medium">{idx + 1}</div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2">
                  <div className="md:col-span-4">
                    <Label className="text-xs">Phase Name</Label>
                    <Input value={p.name} onChange={(e) => updatePhase(idx, 'name', e.target.value)} placeholder="e.g. Prewetting" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Days</Label>
                    <Input type="number" min={1} max={365} value={p.duration_days} onChange={(e) => updatePhase(idx, 'duration_days', parseInt(e.target.value || 0, 10))} />
                  </div>
                  <div className="md:col-span-4">
                    <Label className="text-xs">Description</Label>
                    <Input value={p.description || ''} onChange={(e) => updatePhase(idx, 'description', e.target.value)} placeholder="Optional notes" />
                  </div>
                  <div className="md:col-span-2 flex flex-col gap-1 pt-5">
                    <label className="text-xs flex items-center gap-1">
                      <input type="checkbox" checked={!!p.action_required} onChange={(e) => updatePhase(idx, 'action_required', e.target.checked)} /> Action
                    </label>
                    {activeTab === 'growing' && (
                      <label className="text-xs flex items-center gap-1">
                        <input type="checkbox" checked={!!p.harvest} onChange={(e) => updatePhase(idx, 'harvest', e.target.checked)} /> Harvest
                      </label>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1 pt-6">
                  <button onClick={() => addPhase(idx)} className="text-[#1A4D2E] hover:bg-green-50 p-1 rounded" title="Insert below"><Plus className="w-4 h-4" /></button>
                  <button onClick={() => removePhase(idx)} className="text-red-500 hover:bg-red-50 p-1 rounded" title="Remove"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>

          <Button variant="outline" className="mt-3 w-full" onClick={() => addPhase(phases.length - 1)}>
            <Plus className="w-4 h-4 mr-2" /> Add a phase
          </Button>

          <div className="mt-4 text-sm text-gray-600">
            Total <strong>{activeTab}</strong> cycle: <strong>{totalDays} days</strong> across {phases.length} phase(s).
            {meta[activeTab].is_default && <span className="ml-2 text-gray-400">(currently using default)</span>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CycleConfig;
